import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { formatCivilDate, links, resolveFormattingLocale } from '@eis/core';
import {
  ExportButton,
  EntityHeader,
  ErnLine,
  ErrorState,
  PageLayout,
  RequirePermission,
  usePermissions,
} from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';
import { buildLeadExportLabels } from '../model/export-labels';
import { groupPolicyStatusOf } from '../../group-policies/model/types';
import { ConflictNotice } from '../../policies/ui/ConflictNotice';
import {
  useGroupPolicy,
  useLead,
  useLeadSchema,
  useRefreshLead,
  useRegisterLeadPartners,
  useTransitionLead,
} from '../api/leads';
import {
  acceptBlocker,
  canRegister,
  isEditable,
  isMemberLead,
  kindOf,
  tarifNameOf,
  userTransitions,
} from '../model/leads';
import { isLeadRefusal, refusalMessageOf } from '../model/refusals';
import { LEAD_REASONS, type AvailableTransition } from '../model/types';
import { AcceptLeadDialog } from './AcceptLeadDialog';
import { LeadDraftSections } from './LeadDraftSections';
import { LeadFormDialog } from './LeadFormDialog';
import { LeadHistoryTab } from './LeadHistoryTab';
import { LeadStatusChip } from './LeadStatusChip';
import { TransitionReasonDialog } from './TransitionReasonDialog';

export type LeadTab = 'overview' | 'history';

/**
 * One lead (SDD-013 §8.1.3, R-3): the policy detail's layout with a lead header.
 *
 * # The action bar is the machine's, not the console's
 *
 * `available_transitions` comes from the service, resolved through THIS lead's pinned lifecycle.
 * One button per USER transition, labelled with the target status's tenant name; a transition
 * marked `requires: approve` is gated `leads.approve`, one marked `reasonRequired` opens the
 * reason dialog first; SYSTEM transitions are not offered. Nothing here knows a status code.
 *
 * Accept is `policies.create` (it mints a policy) and is DISABLED WITH THE REASON when §7.2
 * would refuse — not accepted, drafts remain, the agreement not active — so the operator learns
 * what has to happen first. Refresh and Edit need an OPEN, unlocked lead; Register partners the
 * Partner domain's key and a draft to register.
 */
export function LeadDetailPage({
  code,
  tab,
  onTabChange,
}: {
  readonly code: string;
  readonly tab: LeadTab;
  readonly onTabChange: (tab: LeadTab) => void;
}): ReactNode {
  const { t, i18n } = useTranslation('insurance');
  const locale = resolveFormattingLocale(i18n.language);
  const navigate = useNavigate();
  const { can } = usePermissions();
  const leadQuery = useLead(code);
  const schema = useLeadSchema(code);
  const lead = leadQuery.data?.lead;
  const member = isMemberLead(lead);
  const parentCode = lead?.draft?.parentPolicyCode ?? '';
  // The master's status decides whether Accept may run (§7.2 step 5) — only for a member lead.
  const parent = useGroupPolicy(member ? parentCode : '');
  const transition = useTransitionLead();
  const refresh = useRefreshLead();
  const register = useRegisterLeadPartners();
  const [reasonFor, setReasonFor] = useState<AvailableTransition | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  if (leadQuery.isError) {
    return (
      <PageLayout>
        <ErrorState
          description={t('leads.error.detail')}
          details={leadQuery.error.message}
          onRetry={() => {
            void leadQuery.refetch();
          }}
        />
      </PageLayout>
    );
  }
  if (lead === undefined) {
    return (
      <PageLayout header={<Skeleton variant="text" width={320} height={44} />}>
        <Skeleton variant="rounded" height={260} />
      </PageLayout>
    );
  }

  const kind = kindOf(lead);
  const parentStatus = member ? groupPolicyStatusOf(parent.data?.groupPolicy) : undefined;
  const blocker = acceptBlocker(
    lead,
    member && parent.data !== undefined ? parentStatus : undefined,
  );
  const transitions = userTransitions(leadQuery.data?.availableTransitions);
  const policyCode = lead.policyCode ?? '';
  const anyConflict = transition.conflict ?? refresh.conflict ?? register.conflict;
  const lastError = [transition, refresh, register].find(
    (m) => m.isError && m.conflict === null,
  )?.error;

  const take = (next: AvailableTransition, reason?: string): void => {
    transition.mutate(
      { code, to: next.to ?? '', ...(reason === undefined ? {} : { reason }) },
      {
        onSuccess: () => {
          setReasonFor(null);
        },
      },
    );
  };

  return (
    <PageLayout
      header={
        <EntityHeader
          title={lead.code ?? code}
          subtitle={
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <LeadStatusChip status={lead.status} />
              <Chip
                size="small"
                variant="outlined"
                label={t(`leads.kind.${kind}` as never, { defaultValue: kind })}
                data-testid="lead-kind"
              />
            </Stack>
          }
          // Berry has no breadcrumb band: the way back is the header's own link (prompt 24).
          backTo={{
            label: t('leads.title'),
            href: links.insurance.leads(),
            onNavigate: (event) => {
              event.preventDefault();
              void navigate({ href: links.insurance.leads() });
            },
          }}
          meta={[
            {
              label: t('leads.ern'),
              value: <ErnLine ern={lead.ern} copyLabel={t('leads.ernCopy')} />,
            },
            {
              label: t('leads.header.tarif'),
              value: tarifNameOf(lead.draft?.configuration?.resolvedTarifVersionErn),
            },
            {
              label: t('leads.header.validUntil'),
              value:
                lead.validUntil === undefined || lead.validUntil === ''
                  ? t('leads.list.noValidity')
                  : formatCivilDate(lead.validUntil, locale),
            },
            ...(member
              ? [
                  {
                    label: t('leads.header.parent'),
                    value: can(p.groupPolicies.view) ? (
                      <Link
                        href={links.insurance.groupPolicy(parentCode, 'leads')}
                        onClick={(event) => {
                          event.preventDefault();
                          void navigate({ href: links.insurance.groupPolicy(parentCode, 'leads') });
                        }}
                      >
                        {parentCode}
                      </Link>
                    ) : (
                      parentCode
                    ),
                  },
                ]
              : []),
            ...(policyCode !== ''
              ? [
                  {
                    // R-4: the lead's half of the link, once the policy exists.
                    label: t('leads.header.policy'),
                    value: can(p.policies.view) ? (
                      <Link
                        data-testid="lead-policy-link"
                        // The policy starts when the lead does; today has no belief about it yet (SDD-008 §5).
                        href={links.insurance.policy(policyCode, { validAt: lead.effectiveFrom })}
                        onClick={(event) => {
                          event.preventDefault();
                          void navigate({
                            href: links.insurance.policy(policyCode, {
                              validAt: lead.effectiveFrom,
                            }),
                          });
                        }}
                      >
                        {policyCode}
                      </Link>
                    ) : (
                      policyCode
                    ),
                  },
                ]
              : []),
            { label: t('leads.header.createdBy'), value: lead.createdBy ?? '—' },
            { label: t('leads.header.updatedBy'), value: lead.updatedBy ?? '—' },
            // "Stand", never "Version" (SSOT I-6): the head's row version two operators must agree on.
            { label: t('leads.header.state'), value: lead.version ?? '—' },
          ]}
          actions={
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              {/* The lead and its versions in one workbook (SDD-020 §8.3). */}
              <ExportButton
                service="insurance"
                permission={p.exports.create}
                buildDetailRequest={() => ({
                  resource: lead.ern ?? `insuranceservice:lead/${lead.code ?? ''}`,
                  labels: buildLeadExportLabels(t),
                  label: t('leads.exportLabel', { code: lead.code ?? '' }),
                })}
              />
              {transitions.map((next) => {
                const button = (
                  <Button
                    key={next.to}
                    variant="outlined"
                    size="small"
                    disabled={transition.isPending}
                    data-testid={`transition-${next.to ?? ''}`}
                    onClick={() => {
                      if (next.reasonRequired === true) setReasonFor(next);
                      else take(next);
                    }}
                  >
                    {next.name !== undefined && next.name !== '' ? next.name : next.to}
                  </Button>
                );
                return next.requires === 'approve' ? (
                  <RequirePermission key={next.to} anyOf={[p.leads.approve]}>
                    {button}
                  </RequirePermission>
                ) : (
                  <RequirePermission key={next.to} anyOf={[p.leads.update]}>
                    {button}
                  </RequirePermission>
                );
              })}
              <RequirePermission anyOf={[p.policies.create]}>
                <Tooltip title={blocker === null ? '' : t(`leads.accept.blocked.${blocker}`)}>
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={blocker !== null}
                      data-testid="accept-lead"
                      onClick={() => {
                        setAccepting(true);
                      }}
                    >
                      {t('leads.actions.accept')}
                    </Button>
                  </span>
                </Tooltip>
              </RequirePermission>
              {isEditable(lead) && (
                <RequirePermission anyOf={[p.leads.update]}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={refresh.isPending}
                    onClick={() => {
                      refresh.mutate(
                        { code },
                        {
                          onSuccess: () => {
                            setToast(t('leads.refresh.done'));
                          },
                        },
                      );
                    }}
                  >
                    {t('leads.actions.refresh')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setEditing(true);
                    }}
                  >
                    {t('leads.actions.edit')}
                  </Button>
                </RequirePermission>
              )}
              {canRegister(lead) && (
                <RequirePermission anyOf={[p.partners.create]}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={register.isPending}
                    data-testid="register-partners"
                    onClick={() => {
                      register.mutate(
                        { code },
                        {
                          onSuccess: (response) => {
                            setToast(
                              t('leads.register.done', { count: response.registered?.length ?? 0 }),
                            );
                          },
                        },
                      );
                    }}
                  >
                    {t('leads.actions.register')}
                  </Button>
                </RequirePermission>
              )}
            </Stack>
          }
        />
      }
      subHeader={
        <Tabs
          value={tab}
          onChange={(_event, next: LeadTab) => {
            onTabChange(next);
          }}
          aria-label={t('leads.tabs.label')}
          sx={{ width: '100%' }}
        >
          <Tab value="overview" label={t('leads.tabs.overview')} />
          <Tab value="history" label={t('leads.tabs.history')} />
        </Tabs>
      }
    >
      <Stack spacing={2.5}>
        <ConflictNotice
          conflict={anyConflict}
          onReload={() => {
            void leadQuery.refetch();
          }}
        />
        {lastError !== undefined && (
          <Alert severity="error" data-testid="lead-refusal">
            {isLeadRefusal(lastError, LEAD_REASONS.partnerRegistryUnavailable)
              ? t('leads.register.unavailable')
              : refusalMessageOf(lastError)}
          </Alert>
        )}

        {tab === 'overview' ? (
          <LeadDraftSections lead={lead} rules={schema.data?.partnerRoles} />
        ) : (
          <LeadHistoryTab code={code} rules={schema.data?.partnerRoles} />
        )}
      </Stack>

      <TransitionReasonDialog
        transition={reasonFor}
        open={reasonFor !== null}
        pending={transition.isPending}
        onSubmit={(reason) => {
          if (reasonFor !== null) take(reasonFor, reason);
        }}
        onClose={() => {
          setReasonFor(null);
        }}
      />

      <RequirePermission anyOf={[p.policies.create]}>
        <AcceptLeadDialog
          lead={lead}
          open={accepting}
          onClose={() => {
            setAccepting(false);
          }}
          onAccepted={(created) => {
            setAccepting(false);
            // To the new policy, with the way back (SDD-013 §8.1.3): the policy page shows the toast.
            // As of the lead's start — the policy begins then, and today knows nothing of it.
            void navigate({
              href: links.insurance.policy(created, {
                fromLead: code,
                validAt: lead?.effectiveFrom,
              }),
            });
          }}
        />
      </RequirePermission>

      {isEditable(lead) && (
        <RequirePermission anyOf={[p.leads.update]}>
          <LeadFormDialog
            mode={{ kind: 'edit', lead }}
            open={editing}
            onClose={() => {
              setEditing(false);
            }}
            onSaved={() => {
              setEditing(false);
              setToast(t('leads.edit.saved'));
            }}
          />
        </RequirePermission>
      )}

      <Snackbar
        open={toast !== null}
        autoHideDuration={6000}
        message={toast ?? ''}
        onClose={() => {
          setToast(null);
        }}
      />
    </PageLayout>
  );
}
